import { useSortable } from '@dnd-kit/sortable';
import { GripVertical } from 'lucide-react';
import { Link } from 'react-router-dom';
import Colors from '@/constants/colors';
import type { WidgetType } from '@/lib/store';

interface SortableWidgetProps {
  id: WidgetType;
  to: string;
  isFullWidth?: boolean;
  isDragging?: boolean;
  children: React.ReactNode;
}

export default function SortableWidget({ id, to, isFullWidth, isDragging, children }: SortableWidgetProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  // Don't apply transform at all - we use DragOverlay for the dragging visual
  // This prevents grid deformation completely
  // Items will swap positions instantly on drop
  void transform; // Suppress unused variable warning
  void transition;

  const style: React.CSSProperties = {
    opacity: isDragging ? 0.4 : 1,
    gridColumn: isFullWidth ? 'span 2' : undefined,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <button
        {...attributes}
        {...listeners}
        style={gripHandleStyles.button}
        aria-label="Drag to reorder"
      >
        <GripVertical size={16} color={Colors.textSecondary} />
      </button>

      <Link to={to} style={{ textDecoration: 'none', display: 'block' }}>
        {children}
      </Link>
    </div>
  );
}

const gripHandleStyles = {
  button: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    zIndex: 10,
    padding: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'grab',
    touchAction: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,
};
